// ONE RENDERER for the build preview's screen mockups.
//
// The designer (a fleet generation) hands back, per screen, a WIDGET TREE — a
// JSON array of {t, span, …} widgets on a 12-column grid — plus the app's
// identity (product name, nav, accent). This component draws it as a framed
// application window: chrome (sidebar nav, top bar, breadcrumb), the page
// title, then the widgets. It is the showroom's mechanism (one renderer, many
// specs) rebuilt small for cortex's own UI: a business person judges a PICTURE
// of the application, not a list of tables.
//
// Defensive by design: every field is optional, every list is capped, an
// unknown widget type is drawn as a labelled block. A sloppy spec must still
// produce a screen.
//
// Sized at a fixed DESIGN WIDTH and scaled with a CSS transform to whatever box
// it is given, so the same spec renders as a card thumbnail and as a full-size
// view without a second layout.
import React, { useLayoutEffect, useRef, useState } from "react";

export const DESIGN_W = 1120;
export const DESIGN_H = 700;

const S = (v, max = 200) => String(v == null ? "" : v).slice(0, max);
const N = (v) => { const n = Number(String(v ?? "").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) ? n : 0; };
const L = (v, max = 12) => (Array.isArray(v) ? v : []).slice(0, max);
const TONE = {
  ok: { bg: "#dcfce7", fg: "#166534" }, warn: { bg: "#fef3c7", fg: "#92400e" }, crit: { bg: "#fee2e2", fg: "#991b1b" },
  info: { bg: "#e0f2fe", fg: "#075985" }, muted: { bg: "#f1f5f9", fg: "#475569" },
};
const tone = (t) => TONE[t] || TONE.muted;

// ── widgets ──────────────────────────────────────────────────────────────────
function Frame({ title, children, note }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      {title ? <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{S(title, 90)}</div> : null}
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      {note ? <div style={{ fontSize: 10, color: "#64748b" }}>{S(note, 160)}</div> : null}
    </div>
  );
}

function Kpis({ w, accent }) {
  const items = L(w.items, 6);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(1, items.length)}, minmax(0, 1fr))`, gap: 10 }}>
      {items.map((it, i) => (
        <div key={i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", minWidth: 0 }}>
          <div style={{ fontSize: 10, color: "#64748b", lineHeight: 1.3, minHeight: 26 }}>{S(it.k, 34)}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{S(it.v, 16)}</div>
          {it.d ? <div style={{ fontSize: 10, color: String(it.d).trim().startsWith("-") ? "#b91c1c" : accent, marginTop: 2 }}>{S(it.d, 24)}</div> : null}
        </div>
      ))}
    </div>
  );
}

function Chart({ w, accent }) {
  const s = L(w.s, 12).map((x) => ({ k: S(x.k, 22), v: N(x.v), r: S(x.r, 14) }));
  const kind = String(w.chart || "bar");
  const max = Math.max(1, ...s.map((x) => Math.abs(x.v)));
  const W = 480, H = 160, PL = 8, PB = 22;
  let body = null;
  if (kind === "donut") {
    const total = s.reduce((a, x) => a + Math.abs(x.v), 0) || 1;
    let acc = 0;
    const R = 56, C = 2 * Math.PI * R;
    body = (
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r={R} fill="none" stroke="#e2e8f0" strokeWidth="18" />
          {s.map((x, i) => {
            const frac = Math.abs(x.v) / total; const dash = frac * C; const off = -acc * C; acc += frac;
            return <circle key={i} cx="70" cy="70" r={R} fill="none" stroke={accent} strokeOpacity={1 - (i * 0.6) / Math.max(1, s.length)} strokeWidth="18" strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={off} transform="rotate(-90 70 70)" />;
          })}
        </svg>
        <div style={{ fontSize: 10, color: "#334155", display: "grid", gap: 3 }}>
          {s.slice(0, 6).map((x, i) => <div key={i}><span style={{ display: "inline-block", width: 8, height: 8, background: accent, opacity: 1 - (i * 0.6) / Math.max(1, s.length), marginRight: 6, borderRadius: 2 }} />{x.k} <b>{x.r || x.v}</b></div>)}
        </div>
      </div>
    );
  } else if (kind === "line" || kind === "area") {
    const step = s.length > 1 ? (W - PL * 2) / (s.length - 1) : 0;
    const pts = s.map((x, i) => [PL + i * step, H - PB - (Math.abs(x.v) / max) * (H - PB - 10)]);
    const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
    body = (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", height: H }}>
        {[0.25, 0.5, 0.75].map((g) => <line key={g} x1={PL} x2={W - PL} y1={H - PB - g * (H - PB - 10)} y2={H - PB - g * (H - PB - 10)} stroke="#f1f5f9" />)}
        {kind === "area" && pts.length ? <path d={`${d} L${pts[pts.length - 1][0]},${H - PB} L${pts[0][0]},${H - PB} Z`} fill={accent} fillOpacity="0.15" /> : null}
        <path d={d} fill="none" stroke={accent} strokeWidth="2.5" />
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={accent} />)}
        {s.map((x, i) => <text key={i} x={pts[i][0]} y={H - 6} fontSize="9" fill="#64748b" textAnchor={i === 0 ? "start" : i === s.length - 1 ? "end" : "middle"}>{x.k.slice(0, 10)}</text>)}
      </svg>
    );
  } else {
    // bar / column
    const bw = (W - PL * 2) / Math.max(1, s.length);
    body = (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", height: H }}>
        {[0.25, 0.5, 0.75].map((g) => <line key={g} x1={PL} x2={W - PL} y1={H - PB - g * (H - PB - 14)} y2={H - PB - g * (H - PB - 14)} stroke="#f1f5f9" />)}
        {s.map((x, i) => {
          const h = (Math.abs(x.v) / max) * (H - PB - 14);
          return (
            <g key={i}>
              <rect x={PL + i * bw + bw * 0.18} y={H - PB - h} width={bw * 0.64} height={h} rx="3" fill={accent} fillOpacity={x.v < 0 ? 0.45 : 0.9} />
              <text x={PL + i * bw + bw / 2} y={H - PB - h - 3} fontSize="9" fill="#334155" textAnchor="middle">{x.r || ""}</text>
              <text x={PL + i * bw + bw / 2} y={H - 6} fontSize="9" fill="#64748b" textAnchor="middle">{x.k.slice(0, Math.max(4, Math.floor(bw / 6)))}</text>
            </g>
          );
        })}
      </svg>
    );
  }
  return <Frame title={w.title} note={w.note}>{body}</Frame>;
}

function Table({ w }) {
  const cols = L(w.cols, 8).map((c) => S(c, 24));
  const rows = L(w.rows, 6).map((r) => L(r, 8).map((c) => S(c, 28)));
  const chip = Number.isInteger(w.chip) ? w.chip : null;
  const chipTone = (v) => /fail|crit|high|late|overdue|breach|risk/i.test(v) ? "crit" : /warn|review|pending|watch|medium/i.test(v) ? "warn" : /ok|done|on track|pass|low|approved/i.test(v) ? "ok" : "muted";
  return (
    <Frame title={w.title}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5, tableLayout: "fixed" }}>
        <thead><tr>{cols.map((c, i) => <th key={i} style={{ textAlign: "left", color: "#64748b", fontWeight: 600, padding: "4px 6px", borderBottom: "1px solid #e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? "#f8fafc" : "#fff" }}>
              {r.map((c, j) => (
                <td key={j} style={{ padding: "5px 6px", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {chip === j ? <span style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 999, background: tone(chipTone(c)).bg, color: tone(chipTone(c)).fg }}>{c}</span> : c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Frame>
  );
}

function Queue({ w }) {
  return (
    <Frame title={w.title} note={w.sub}>
      <div style={{ display: "grid", gap: 6 }}>
        {L(w.items, 6).map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px", borderRadius: 6, background: it.sel ? "#eef2ff" : "#f8fafc", border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: 9.5, color: "#64748b", fontFamily: "ui-monospace, monospace", minWidth: 54 }}>{S(it.id, 12)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{S(it.t, 60)}</div>
              <div style={{ fontSize: 9.5, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{S(it.m, 90)}</div>
            </div>
            {it.b ? <span style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 999, background: tone(it.tone).bg, color: tone(it.tone).fg, whiteSpace: "nowrap" }}>{S(it.b, 18)}</span> : null}
          </div>
        ))}
      </div>
    </Frame>
  );
}

function Detail({ w }) {
  return (
    <Frame title={w.title} note={w.note}>
      {w.head ? <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>{S(w.head, 80)}</div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 14px" }}>
        {L(w.fields, 10).map((f, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10.5, borderBottom: "1px dashed #e2e8f0", padding: "3px 0", minWidth: 0 }}>
            <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>{S(f.k, 26)}</span>
            <span style={{ color: "#0f172a", fontWeight: 600, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{S(f.v, 30)}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function Alerts({ w }) {
  return (
    <Frame title={w.title}>
      <div style={{ display: "grid", gap: 5 }}>
        {L(w.items, 6).map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 10.5 }}>
            <span style={{ marginTop: 3, width: 8, height: 8, borderRadius: 999, background: tone(it.sev).fg, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: "#0f172a" }}>{S(it.t, 70)}</div>
              <div style={{ color: "#64748b" }}>{S(it.m, 110)}</div>
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function Gauges({ w, accent }) {
  return (
    <Frame title={w.title} note={w.note}>
      <div style={{ display: "grid", gap: 7 }}>
        {L(w.items, 6).map((it, i) => {
          const v = Math.max(0, Math.min(120, N(it.v)));
          return (
            <div key={i} style={{ fontSize: 10.5 }}>
              <div style={{ display: "flex", justifyContent: "space-between", color: "#334155" }}><span>{S(it.k, 30)}</span><b>{Math.round(v)}%</b></div>
              <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}><div style={{ width: `${Math.min(100, v)}%`, height: "100%", background: v > 100 ? "#b91c1c" : accent }} /></div>
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

function Heatmap({ w, accent }) {
  const cols = L(w.cols, 10).map((c) => S(c, 10)); const rows = L(w.rows, 8).map((r) => S(r, 22));
  const cells = L(w.cells, 8);
  return (
    <Frame title={w.title} note={w.note}>
      <table style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 9.5, width: "100%" }}>
        <thead><tr><th />{cols.map((c, i) => <th key={i} style={{ color: "#64748b", fontWeight: 500 }}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}><td style={{ color: "#334155", whiteSpace: "nowrap", paddingRight: 6 }}>{r}</td>
              {cols.map((_, j) => { const v = Math.max(0, Math.min(100, N((cells[i] || [])[j]))); return <td key={j} style={{ background: accent, opacity: 0.15 + (v / 100) * 0.85, height: 18, borderRadius: 3, color: v > 55 ? "#fff" : "#0f172a", textAlign: "center" }}>{Math.round(v)}</td>; })}
            </tr>
          ))}
        </tbody>
      </table>
    </Frame>
  );
}

function Stepper({ w, accent }) {
  return (
    <Frame title={w.title}>
      <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
        {L(w.items, 7).map((it, i) => {
          const st = String(it.st || "todo");
          return (
            <div key={i} style={{ flex: 1, minWidth: 0 }}>
              <div style={{ height: 4, borderRadius: 999, background: st === "todo" ? "#e2e8f0" : accent, opacity: st === "active" ? 0.6 : 1 }} />
              <div style={{ fontSize: 10.5, fontWeight: 600, color: st === "todo" ? "#94a3b8" : "#0f172a", marginTop: 5 }}>{S(it.l, 26)}</div>
              {it.m ? <div style={{ fontSize: 9.5, color: "#64748b" }}>{S(it.m, 50)}</div> : null}
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

const STAGE_TONE = { human: "warn", decide: "info", risk: "crit", act: "ok", learn: "info" };
function Flow({ w, accent }) {
  return (
    <Frame title={w.title}>
      <div style={{ display: "grid", gap: 8 }}>
        {L(w.lanes, 4).map((lane, li) => (
          <div key={li} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 9.5, color: "#64748b", minWidth: 70, textAlign: "right" }}>{S(lane.k, 18)}</div>
            {L(lane.nodes, 8).map((n, i) => (
              <React.Fragment key={i}>
                <div style={{ fontSize: 10, padding: "5px 8px", borderRadius: 6, border: `1px solid ${accent}`, background: n.s === "human" ? tone("warn").bg : "#fff", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }} title={S(n.s, 12)}>
                  {S(n.l, 26)}
                </div>
                {i < L(lane.nodes, 8).length - 1 ? <span style={{ color: "#94a3b8", fontSize: 12 }}>→</span> : null}
              </React.Fragment>
            ))}
          </div>
        ))}
        {L(w.gates, 4).length ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {L(w.gates, 4).map((g, i) => <span key={i} style={{ fontSize: 9.5, padding: "2px 7px", borderRadius: 999, background: tone(g.tone || "warn").bg, color: tone(g.tone || "warn").fg }}>{S(g.l, 26)}{g.d ? ` · ${S(g.d, 40)}` : ""}</span>)}
          </div>
        ) : null}
      </div>
    </Frame>
  );
}

function Steps({ w, accent }) {
  return (
    <Frame title={w.title} note={w.note}>
      <ol style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 4, fontSize: 10.5 }}>
        {L(w.items, 7).map((it, i) => <li key={i} style={{ color: "#334155" }}><b style={{ color: accent }}>{S(it.k, 30)}</b>{it.v ? ` — ${S(it.v, 110)}` : ""}</li>)}
      </ol>
    </Frame>
  );
}

function Cards({ w }) {
  return (
    <Frame title={w.title}>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, L(w.items, 6).length))}, minmax(0,1fr))`, gap: 8 }}>
        {L(w.items, 6).map((it, i) => (
          <div key={i} style={{ border: "1px solid #e2e8f0", borderLeft: `3px solid ${tone(it.tone).fg}`, borderRadius: 6, padding: 8, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>{S(it.h, 50)}</div>
            <div style={{ fontSize: 10, color: "#334155", marginTop: 3 }}>{S(it.b, 140)}</div>
            {it.m ? <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 3 }}>{S(it.m, 60)}</div> : null}
          </div>
        ))}
      </div>
    </Frame>
  );
}

function Sparkrows({ w, accent }) {
  return (
    <Frame title={w.title}>
      <div style={{ display: "grid", gap: 5 }}>
        {L(w.items, 7).map((it, i) => {
          const s = L(it.s, 24).map(N); const max = Math.max(1, ...s.map(Math.abs)); const step = s.length > 1 ? 120 / (s.length - 1) : 0;
          const d = s.map((v, j) => `${j ? "L" : "M"}${j * step},${24 - (Math.abs(v) / max) * 22}`).join(" ");
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 120px 70px 60px", gap: 10, alignItems: "center", fontSize: 10.5 }}>
              <span style={{ color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{S(it.k, 34)}</span>
              <svg width="120" height="26" viewBox="0 0 120 26"><path d={d} fill="none" stroke={accent} strokeWidth="1.8" /></svg>
              <b style={{ color: "#0f172a", textAlign: "right" }}>{S(it.v, 12)}</b>
              <span style={{ color: String(it.d || "").trim().startsWith("-") ? "#b91c1c" : "#166534", textAlign: "right" }}>{S(it.d, 10)}</span>
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

function Filters({ w, accent }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px" }}>
      {L(w.items, 6).map((f, i) => <span key={i} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, border: "1px solid #e2e8f0", color: "#334155" }}>{S(f, 28)} ▾</span>)}
      <span style={{ flex: 1 }} />
      {w.count != null ? <span style={{ fontSize: 10, color: "#64748b" }}>{S(w.count, 30)}</span> : null}
      {L(w.actions, 3).map((a, i) => <span key={i} style={{ fontSize: 10, padding: "3px 9px", borderRadius: 6, background: i === 0 ? accent : "#f1f5f9", color: i === 0 ? "#fff" : "#334155" }}>{S(a, 24)}</span>)}
    </div>
  );
}

function Actionbar({ w, accent }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#0f172a", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ color: "#e2e8f0", fontSize: 11, fontWeight: 600, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{S(w.title, 80)}{w.meta ? <span style={{ color: "#94a3b8", fontWeight: 400 }}> · {S(w.meta, 60)}</span> : null}</div>
      {L(w.items, 3).map((a, i) => <span key={i} style={{ fontSize: 10.5, padding: "5px 10px", borderRadius: 6, border: "1px solid #475569", color: "#e2e8f0" }}>{S(a, 22)}</span>)}
      {w.primary ? <span style={{ fontSize: 10.5, padding: "5px 12px", borderRadius: 6, background: accent, color: "#fff", fontWeight: 600 }}>{S(w.primary, 24)}</span> : null}
    </div>
  );
}

function Note({ w }) {
  return <div style={{ fontSize: 10, color: "#64748b", padding: "2px 4px" }}>{S(w.text, 220)}</div>;
}

function Unknown({ w }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 8, padding: 12, height: "100%", boxSizing: "border-box", color: "#64748b", fontSize: 10.5 }}>
      <b style={{ color: "#334155" }}>{S(w.title || w.t, 60)}</b>
      <div>{S(w.t, 20)} widget</div>
    </div>
  );
}

const WIDGETS = { kpis: Kpis, chart: Chart, table: Table, queue: Queue, detail: Detail, alerts: Alerts, gauges: Gauges, heatmap: Heatmap, stepper: Stepper, flow: Flow, steps: Steps, cards: Cards, sparkrows: Sparkrows, filters: Filters, actionbar: Actionbar, note: Note };

// ── the screen ───────────────────────────────────────────────────────────────
export function ScreenCanvas({ app, screen }) {
  const a = app || {};
  const accent = /^#[0-9a-fA-F]{6}$/.test(String(a.accent || "")) ? a.accent : "#4f46e5";
  const nav = L(a.nav, 6).map((x) => S(x, 24));
  const widgets = L(screen?.w, 8);
  return (
    <div style={{ width: DESIGN_W, minHeight: DESIGN_H, background: "#f1f5f9", display: "flex", fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif", color: "#0f172a", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 0 rgba(15,23,42,.06)" }}>
      {/* sidebar */}
      <div style={{ width: 176, background: "#0f172a", color: "#e2e8f0", padding: 14, boxSizing: "border-box", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: accent, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, color: "#fff" }}>{S(a.logo, 2) || "CX"}</div>
          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{S(a.product_name, 26) || "Cortex app"}</div>
        </div>
        {nav.map((n, i) => (
          <div key={i} style={{ fontSize: 11, padding: "7px 9px", borderRadius: 6, marginBottom: 2, background: i === 0 ? "rgba(255,255,255,.12)" : "transparent", color: i === 0 ? "#fff" : "#cbd5e1", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n}</div>
        ))}
        <div style={{ position: "absolute", bottom: 0 }} />
      </div>
      {/* main */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ height: 40, background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", padding: "0 16px", gap: 12, fontSize: 11 }}>
          <span style={{ color: "#64748b" }}>{S(a.product_name, 26)} / {S(nav[0], 20)} / <span style={{ color: "#0f172a" }}>{S(screen?.title, 40)}</span></span>
          <span style={{ flex: 1 }} />
          <span style={{ width: 180, height: 22, borderRadius: 6, border: "1px solid #e2e8f0", color: "#94a3b8", fontSize: 10, display: "flex", alignItems: "center", padding: "0 8px" }}>Search…</span>
          <span style={{ width: 22, height: 22, borderRadius: 999, background: accent, color: "#fff", fontSize: 9.5, display: "grid", placeItems: "center", fontWeight: 700 }}>{S(a.user_name, 1).toUpperCase() || "A"}</span>
          <span style={{ color: "#334155", whiteSpace: "nowrap" }}>{S(a.user_name, 22)}<span style={{ color: "#94a3b8" }}> · {S(a.user_role, 24)}</span></span>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{S(screen?.title, 70) || "Screen"}</div>
            {screen?.caption ? <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{S(screen.caption, 160)}</div> : null}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 12, alignItems: "stretch" }}>
            {widgets.map((w, i) => {
              const Cmp = WIDGETS[String(w.t)] || Unknown;
              const span = Math.max(1, Math.min(12, Number(w.span) || 12));
              return <div key={i} style={{ gridColumn: `span ${span} / span ${span}`, minWidth: 0 }}><Cmp w={w} accent={accent} /></div>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Scales the design-width canvas into whatever width the parent gives it.
export default function ScreenMockup({ app, screen, className = "", style = {}, ...rest }) {
  const box = useRef(null);
  const [scale, setScale] = useState(0.3);
  const [h, setH] = useState(DESIGN_H * 0.3);
  const inner = useRef(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const fit = () => {
      const w = el.clientWidth || DESIGN_W;
      const s = Math.min(1, w / DESIGN_W);
      setScale(s);
      const ih = inner.current ? inner.current.offsetHeight : DESIGN_H;
      setH(Math.max(DESIGN_H, ih) * s);
    };
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [app, screen]);
  return (
    <div ref={box} className={className} style={{ width: "100%", height: h, overflow: "hidden", position: "relative", ...style }} {...rest}>
      <div ref={inner} style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: DESIGN_W, position: "absolute", top: 0, left: 0 }}>
        <ScreenCanvas app={app} screen={screen} />
      </div>
    </div>
  );
}
